﻿import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Form, Modal, Button } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import Select from "react-select";
import { v4 as uuidv4 } from "uuid";
import { isObject } from "lodash";

import { formatMilesDecimalNew, hasPermission, toast } from "@/services/Utils";
import { useIrrigation } from "@/hooks/maintainers/application_orders";
import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";
import { customStyles } from "@/components/ReactSelect/NoOptions.styles";
import Fetching from "@/components/Fetching/Fetching";

import IrrigationsBusinessCenter from "./IrrigationsBusinessCenter";
import { base_element } from "../irrigations.constants";

import classes from "../../../maintainers.module.scss";

const IrrigationsForm = ({ confirmSave, isLoadingSave, permissions, estates_parents, estates, business_center }) => {
	const { addToast } = useToasts();
	const location = useLocation();
	const { option, irrigation_id } = useParams();
	const navigate = useNavigate();

	const [data, setData] = useState({ ...base_element });

	const { data: response, isLoading } = useIrrigation(irrigation_id);

	useEffect(() => {
		if (response) {
			setData(response.detail);
		} else {
			let baseElement = { ...base_element };

			if (estates_parents && estates_parents.length === 1) {
				baseElement.estates_parents = [estates_parents[0].value];
			}
			if (estates && estates.length === 1) {
				baseElement.estates = [estates[0].value];
			}

			setData(baseElement);
		}

		// eslint-disable-next-line
	}, [response]);

	const handleClose = () => {
		if (location.state) {
			navigate(location.state);
			return;
		}

		navigate("/configuracion/riegos");
	};

	const validSave = () => {
		let validItems = true;

		if (!data.name) {
			toast(addToast, "Debe ingresar un nombre para guardar.", "error");
			return false;
		}

		if (data.businessCenters.length === 0) {
			toast(addToast, "Debe ingresar al menos un centro de costo.", "error");
			return false;
		}

		data.businessCenters.forEach((item) => {
			const floatTotalHa = item.total_ha ? String(item.total_ha).replace(",", ".") : "";
			const floatHa = item.ha ? String(item.ha).replace(",", ".") : "";

			if (!item.business_center_id || !item.ha) {
				toast(addToast, "Debe completar todos los campos de los centros de costo.", "error");
				validItems = false;
			}

			if (floatHa && floatHa <= 0) {
				toast(addToast, "El campo superficie riego debe ser mayor a 0.", "error");
				validItems = false;
			}

			if (floatHa && Number(floatHa) > Number(floatTotalHa)) {
				toast(addToast, "El campo superficie riego no puede ser mayor a la superficie del centro de costo.", "error");
				validItems = false;
			}
		});

		if (!validItems) {
			return false;
		}

		return true;
	};

	const handleInputChange = (index, key, value) => {
		const businessCenters = [...data.businessCenters];

		if (key === "ha") {
			value = String(value).replaceAll(".", "");
		}

		if (isObject(value) && value.value) {
			if (key === "business_center_id") {
				businessCenters[index]["total_ha"] = value.area ? value.area : "";
			}

			value = value.value;
		}

		businessCenters[index][key] = value;

		setData({
			...data,
			businessCenters,
		});
	};

	const handleSave = () => {
		if (validSave()) {
			confirmSave(data);
		}
	};

	const handleAddRow = () => {
		const newBusinessCenter = [
			...data.businessCenters,
			{
				hash: uuidv4(),
				business_center_id: "",
				total_ha: "",
				ha: "",
			},
		];

		setData({
			...data,
			businessCenters: newBusinessCenter,
		});
	};

	const handleDelete = (index) => {
		const businessCenters = [...data.businessCenters];
		businessCenters.splice(index, 1);
		setData({
			...data,
			businessCenters,
		});
	};

	return (
		<Modal show={option ? true : false} onHide={handleClose} backdrop="static" keyboard={false} className={classes.main_form} size="lg">
			<Modal.Header closeButton>
				<h4 className={classes.modal_title}>Sectores de Riego</h4>
			</Modal.Header>

			<Modal.Body>
				{isLoading || isLoadingSave ? (
					<Fetching topMargin={"1em"} />
				) : (
					<>
						<Row className={classes.row_form}>
							<Col md={6} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Nombre</Form.Label>
									<Form.Control
										value={data ? data.name : ""}
										onChange={(e) =>
											setData({
												...data,
												name: e.currentTarget.value,
											})
										}
										type="text"
									/>
								</Form.Group>
							</Col>

							<Col md={6} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Área Total</Form.Label>
									<Form.Control
										value={
											data &&
											data.businessCenters &&
											data.businessCenters.length > 0 &&
											formatMilesDecimalNew(
												String(data.businessCenters.reduce((acc, item) => acc + Number(String(item.ha).replace(",", ".")), 0)).replace(
													".",
													",",
												),
											)
										}
										readOnly
										type="text"
									/>
								</Form.Group>
							</Col>

							<Col md={6} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Campo</Form.Label>
									<Select
										isMulti
										value={
											estates_parents && estates_parents.filter((ep) => data.estates_parents && data.estates_parents.includes(ep.value))
										}
										placeholder={<div>Seleccione Campo</div>}
										options={estates_parents ? estates_parents : []}
										onChange={(value) => {
											let newEstatesParents = [];
											value &&
												value.map((estate_parent) => {
													newEstatesParents.push(estate_parent.value);
													return null;
												});

											let availableEstates = estates.filter((e) => newEstatesParents.includes(e.estate_parent_id));

											setData({
												...data,
												estates_parents: newEstatesParents,
												estates: availableEstates.map((e) => e.value),
											});
										}}
										components={{
											NoOptionsMessage,
										}}
										styles={customStyles}
									/>
								</Form.Group>
							</Col>

							<Col md={6} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Bodega</Form.Label>
									<Select
										isMulti
										value={estates && estates.filter((et) => data.estates && data.estates.includes(et.value))}
										placeholder={<div>Seleccione Bodega</div>}
										options={estates ? estates.filter((e) => data.estates_parents.includes(e.estate_parent_id)) : []}
										onChange={(value) => {
											let newEstates = [];
											value &&
												value.map((estate) => {
													newEstates.push(estate.value);
													return null;
												});

											setData({
												...data,
												estates: newEstates,
											});
										}}
										components={{
											NoOptionsMessage,
										}}
										styles={customStyles}
									/>
								</Form.Group>
							</Col>
						</Row>

						<IrrigationsBusinessCenter
							data={data}
							handleInputChange={handleInputChange}
							handleAddRow={handleAddRow}
							handleDelete={handleDelete}
							business_center={business_center}
						/>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{(irrigation_id ? hasPermission("edit", permissions) : hasPermission("create", permissions)) && !isLoading && (
					<Button variant="success" onClick={handleSave} disabled={isLoading || (isLoadingSave && true)}>
						GUARDAR
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default IrrigationsForm;
